import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Check if we're in production mode
const isProduction = import.meta.env.PROD;

// Better error handling for missing environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables:');
  console.error('VITE_SUPABASE_URL:', supabaseUrl ? '✓ Set' : '✗ Missing');
  console.error('VITE_SUPABASE_ANON_KEY:', supabaseAnonKey ? '✓ Set' : '✗ Missing');
  
  if (!isProduction) {
    console.error('Please check your .env.local file and ensure these variables are set.');
    throw new Error('Missing Supabase environment variables. Check console for details.');
  } else {
    console.warn('⚠️ Production mode: Running with limited functionality due to missing Supabase configuration');
  }
}

// Create a mock Supabase client for when credentials are missing in production
const createMockSupabaseClient = (): SupabaseClient => {
  const mockClient = {
    auth: {
      getSession: async () => ({ data: { session: null }, error: null }),
      signUp: async () => ({ data: { user: null, session: null }, error: { message: 'Supabase not configured' } }),
      signInWithPassword: async () => ({ data: { user: null, session: null }, error: { message: 'Supabase not configured' } }),
      signOut: async () => ({ error: null }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
      resetPasswordForEmail: async () => ({ data: null, error: { message: 'Supabase not configured' } }),
      updateUser: async () => ({ data: { user: null }, error: { message: 'Supabase not configured' } }),
    },
    from: () => ({
      select: () => ({
        eq: () => ({
          single: async () => ({ data: null, error: { message: 'Supabase not configured' } }),
          order: () => ({
            limit: async () => ({ data: [], error: null }),
          }),
        }),
        order: () => ({
          limit: async () => ({ data: [], error: null }),
        }),
        limit: async () => ({ data: [], error: null }),
      }),
      insert: async () => ({ data: null, error: { message: 'Supabase not configured' } }),
      update: async () => ({ data: null, error: { message: 'Supabase not configured' } }),
      delete: async () => ({ data: null, error: { message: 'Supabase not configured' } }),
      upsert: async () => ({ data: null, error: { message: 'Supabase not configured' } }),
    }),
    storage: {
      from: () => ({
        upload: async () => ({ data: null, error: { message: 'Supabase not configured' } }),
        download: async () => ({ data: null, error: { message: 'Supabase not configured' } }),
        getPublicUrl: () => ({ data: { publicUrl: '' } }),
        remove: async () => ({ data: null, error: { message: 'Supabase not configured' } }),
        list: async () => ({ data: [], error: null }),
      }),
    },
  } as unknown as SupabaseClient;
  
  return mockClient;
};

// Create Supabase client or mock client
export const supabase = (!supabaseUrl || !supabaseAnonKey) && isProduction
  ? createMockSupabaseClient()
  : createClient(supabaseUrl, supabaseAnonKey);

// Avoid eager getSession() on module load — it competes with LCP and duplicates useAuth.
if (!supabaseUrl || !supabaseAnonKey) {
  if (isProduction) {
    console.warn('⚠️ Supabase not configured - running in limited mode');
  }
}